import { CrewCertificate } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';

export const getSeamanDocs = (cert: CrewCertificate, employees: any[]) => {
  const employee = employees.find((e: any) => e.id === cert.employeeId!);
  return employee?.documents?.filter((doc: any) =>
    doc.documentType.toLowerCase().includes('seaman') ||
    doc.documentType.toLowerCase().includes('marine passport') ||
    doc.documentType.toLowerCase().includes('book')
  ) || [];
};

export const getCertificateExpiryStatus = (cert: CrewCertificate) => {
  const expiryDate = new Date(cert.expiryDate);
  const now = new Date();
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(now.getMonth() + 3);
  if (expiryDate < now) return 'expired';
  if (expiryDate <= threeMonthsFromNow) return 'expiring_soon';
  return 'valid';
};

export const getExpiryStats = (filteredCertificates: CrewCertificate[], employees: any[]) => {
  let expiredCount = 0;
  let expiringSoonCount = 0;
  let totalDocuments = 0;
  filteredCertificates.forEach(cert => {
    const seamanDocs = getSeamanDocs(cert, employees);
    totalDocuments += seamanDocs.length;
    seamanDocs.forEach((doc: any) => {
      if (doc.status === 'expired') expiredCount++;
      else if (doc.status === 'expiring_soon') expiringSoonCount++;
    });
  });
  return { expiredCount, expiringSoonCount, totalDocuments };
};

type EmployeeLookup = (employeeId: string) => string;

export const buildExportData = (
  filteredCertificates: CrewCertificate[],
  employees: any[],
  getEmployeeCode: EmployeeLookup,
  getEmployeeName: EmployeeLookup,
  getEmployeeRank: EmployeeLookup,
) => {
  return filteredCertificates.map(cert => {
    const seamanDocs = getSeamanDocs(cert, employees);
    return {
      'Employee Code': getEmployeeCode(cert.employeeId!),
      'Employee Name': getEmployeeName(cert.employeeId!),
      'Rank': getEmployeeRank(cert.employeeId!),
      'Document Type': cert.courseType,
      'Profession': cert.profession,
      'Issue Date': cert.issueDate,
      'Expiry Date': cert.expiryDate,
      'Certificate Number': cert.certificateNumber,
      'Capacity': cert.capacity,
      'Limitation': cert.limitation,
      'Status': cert.status,
      'Expiry Status': getCertificateExpiryStatus(cert),
      'Documents Count': seamanDocs.length,
      'Expired Documents': seamanDocs.filter((doc: any) => doc.status === 'expired').length,
      'Expiring Documents': seamanDocs.filter((doc: any) => doc.status === 'expiring_soon').length,
    };
  });
};

export const handleExportDocuments = (
  filteredCertificates: CrewCertificate[],
  employees: any[],
  getEmployeeCode: EmployeeLookup,
  getEmployeeName: EmployeeLookup,
  getEmployeeRank: EmployeeLookup,
) => {
  const data = buildExportData(filteredCertificates, employees, getEmployeeCode, getEmployeeName, getEmployeeRank);
  exportToExcel(data, `crew_and_cars_documents_${new Date().toISOString().split('T')[0]}`);
};

export const handlePrintDocuments = (
  filteredCertificates: CrewCertificate[],
  employees: any[],
  getEmployeeName: EmployeeLookup,
) => {
  const exportData = filteredCertificates.map(cert => {
    const employee = employees.find((e: any) => e.id === cert.employeeId!);
    const seamanDocs = getSeamanDocs(cert, employees);
    return {
      'Employee Name': getEmployeeName(cert.employeeId!),
      'Employee Code': employee?.code || 'N/A',
      'Rank': employee?.rank || 'N/A',
      'Total Documents': seamanDocs.length,
      'Valid Documents': seamanDocs.filter((doc: any) => doc.status === 'valid').length,
      'Expired Documents': seamanDocs.filter((doc: any) => doc.status === 'expired').length,
      'Expiring Documents': seamanDocs.filter((doc: any) => doc.status === 'expiring_soon').length,
    };
  });
  printAsExcel(exportData, 'Crew and Cars Documents');
};
